import React, { useEffect, useRef, useState } from 'react';
import { MapPin, ChevronDown, Check } from 'lucide-react';
import { useRegion, REGION_OPTIONS } from '../contexts/RegionContext';

const Flag = ({ code }) => (
  code === 'un'
    ? <span className="inline-block w-5 h-[15px] rounded-sm bg-gradient-to-br from-gray-500 to-orange-500" aria-label="global" />
    : <img src={`https://flagcdn.com/24x18/${code}.png`} srcSet={`https://flagcdn.com/48x36/${code}.png 2x`} width={20} height={15} alt={code} className="rounded-sm border border-gray-900/40" loading="lazy" />
);

const RegionSwitcher = ({ compact = false }) => {
  const { code, region, setRegionCode } = useRegion();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const cur = REGION_OPTIONS.find(r => r.code === code) || REGION_OPTIONS[0];

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 ${compact ? 'px-2.5 py-2 text-[11px]' : 'px-3 py-2.5 text-xs'} text-gray-700 hover:text-green-700 bg-white border border-gray-300 hover:border-green-500 rounded transition-colors font-bold uppercase tracking-wider`}
        aria-label="Select region"
      >
        <MapPin className="w-3.5 h-3.5" />
        <Flag code={cur.flag} />
        <span className="hidden sm:inline">{cur.code.toUpperCase()}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-2 w-72 bg-[#1a1f2e] border border-gray-900/60 rounded-md shadow-2xl shadow-black/60 z-50 overflow-hidden animate-fadeIn">
          <div className="px-4 py-3 text-[10px] text-orange-400 font-bold uppercase tracking-[0.2em] border-b border-gray-900/40">Select Your Region</div>
          <div className="max-h-80 overflow-y-auto">
            {REGION_OPTIONS.map(r => (
              <button
                key={r.code}
                onClick={() => { setRegionCode(r.code); setOpen(false); }}
                className={`w-full flex items-center justify-between gap-2 px-4 py-2.5 text-[13px] text-left transition-colors ${r.code === code ? 'bg-gray-900/30 text-green-300' : 'text-gray-300 hover:bg-gray-900/20 hover:text-green-400'}`}
              >
                <span className="flex items-center gap-2.5">
                  <Flag code={r.flag} />
                  <span>{r.name}</span>
                </span>
                {r.code === code && <Check className="w-3.5 h-3.5 text-green-400" />}
              </button>
            ))}
          </div>
          {region && (
            <div className="px-4 py-3 bg-[#0e1116] border-t border-gray-900/40 text-[11px] text-gray-400 space-y-0.5">
              <div><span className="text-orange-400 font-bold">{region.phone}</span></div>
              <div className="truncate">{region.address}</div>
              <div className="text-gray-500">{region.currency} · {region.timezone}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RegionSwitcher;
